<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import { useGlobalStore } from "@stores/global.js";
import { useShopifyStore } from "@stores/shopify.js";
import Logo from "@components/logos/Logo.vue";
import MagnifyingGlass from "@components/icons/MagnifyingGlass.vue";
import Cart from "@components/icons/Cart.vue";
import User from "@components/icons/User.vue";
import BurgerMenu from "@components/icons/BurgerMenu.vue";
import Close from "@components/icons/Close.vue";
import Menu from "@components/global/Menu.vue";
import Search from "@components/global/Search.vue";

const global = useGlobalStore();
const shopify = useShopifyStore();
const headerRef = ref(null);
const menuRef = ref(null);
const searchRef = ref(null);

const { activate, deactivate } = useFocusTrap(headerRef);

const setupFocusTrap = async () => {
	if (global.mobile) {
		await nextTick();
		if (headerRef.value && menuRef.value) {
			activate();
		}
	} else {
		deactivate();
	}
};

const handleKeydown = (e) => {
	if (e.key === "Escape" && global.mobile) {
		global.toggleMobile();
	}
};

onMounted(() => {
	document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
	document.removeEventListener("keydown", handleKeydown);
});

watch(() => global.mobile, setupFocusTrap);

const searchQuery = computed({
	get() {
		return shopify.search.query;
	},
	set(value) {
		shopify.setSearchQuery(value);
	},
});

const onSearchFocus = () => {
	global.toggleSearch(true);
};
</script>

<template>
	<header
		ref="headerRef"
		class="bg-theme text-theme sticky top-0 z-10 border-b border-(--theme-surface)/10 transition-colors"
	>
		<div class="container">
			<div class="flex h-(--header-height) items-center justify-between">
				<div class="flex grow basis-0 items-center">
					<button
						@click="global.toggleMobile()"
						class="flex size-32 items-center justify-center transition-opacity duration-300 hover:opacity-50"
						:aria-label="global.mobile ? 'Close menu' : 'Open menu'"
					>
						<BurgerMenu class="size-24" v-if="!global.mobile" />
						<Close class="size-24" v-else />
					</button>
				</div>
				<router-link
					:to="{ name: 'index' }"
					@click="
						global.toggleMobile(false);
						global.toggleSearch(false);
						shopify.setSearchQuery('');
						global.toggleCart(false);
					"
					aria-label="Home"
				>
					<Logo class="h-28 w-auto sm:h-40 lg:h-40" />
				</router-link>
				<ul class="flex grow basis-0 items-center justify-end">
					<li class="mr-8 hidden lg:block">
						<div class="relative">
							<label for="header-search" class="sr-only">
								Search products
							</label>
							<input
								id="header-search"
								type="text"
								v-model="searchQuery"
								@focus="onSearchFocus"
								placeholder="Search products"
								class="body-md h-40 w-250 rounded-lg bg-(--theme-surface)/5 pr-40 pl-16 text-(--theme-surface) placeholder:text-(--theme-surface)/50 focus:border-(--theme-surface)/40 focus:outline-none xl:w-336"
							/>
							<MagnifyingGlass
								class="pointer-events-none absolute top-1/2 right-12 size-16 -translate-y-1/2 text-(--theme-surface)/40"
							/>
						</div>
					</li>
					<li class="lg:hidden">
						<button
							class="flex size-28 items-center justify-center md:size-40"
							@click="global.toggleSearch()"
							aria-label="Search"
						>
							<MagnifyingGlass class="size-15" />
						</button>
					</li>
					<li>
						<a
							class="flex size-28 items-center justify-center md:size-40"
							href="https://account.ghostvision.com"
							target="_blank"
						>
							<span class="sr-only">Log in to your account</span>
							<User class="size-20" />
						</a>
					</li>
					<li>
						<button
							class="flex size-28 items-center justify-center md:size-40"
							@click="global.toggleCart()"
							aria-label="Cart"
						>
							<Cart class="size-20" />
						</button>
					</li>
				</ul>
			</div>
		</div>
		<Menu ref="menuRef" />
		<Search ref="searchRef" />
	</header>
</template>
